import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
	Flex,
	Box,
	FormControl,
	Input,
	Textarea,
	Stack,
	Button,
	useToast,
	Text,
	Checkbox,
	useDisclosure,
	Tooltip,
	Table,
	Thead,
	Tr,
	Th,
	Tbody,
	Td,
	Heading,
	Card,
	CardBody,
	Badge,
	Center,
	VStack,
	Icon,
} from "@chakra-ui/react";
import moment from "moment";
import { FaBell } from "react-icons/fa";
import { FiBell, FiFileText, FiInfo, FiSend } from "react-icons/fi";

import { ModalFieldLabel, ModalFooterActions, PropertyModalSection, PropertyModalShell } from "./shared";
import { sendMassNotificationsAsync } from "../../redux/user/userSlice";
import { fullDateTimeFormat } from "../../utils/types";

export default function NotificationsTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const initialNotificationInformation = {
		title: "",
		message: "",
		recipients: {
			type: "property",
			propertyId: property.id,
			notifyOwner: false,
		},
	};
	const [notificationInformation, setNotificationInformation] = useState(initialNotificationInformation);
	const notifications = property.notifications || [];
	const { isOpen, onOpen, onClose } = useDisclosure();

	const dispatch = useDispatch();
	const toast = useToast();

	const handleCloseModal = () => {
		onClose();
		setNotificationInformation(initialNotificationInformation);
	};

	const handleSendNotification = () => {
		console.log("sending notification:", notificationInformation);
		dispatch(sendMassNotificationsAsync({ notification: notificationInformation }))
			.then((r) => {
				console.log(r);
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Mass notification sent!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
					setNotificationInformation({});
				}
			})
			.finally(() => {
				onClose();
				setNotificationInformation(initialNotificationInformation);
			});
	};

	const handleNotificationInfoChange = (e) => {
		console.log(e.target.value);
		setNotificationInformation({ ...notificationInformation, [e.target.name]: e.target.value });
		console.log("sending notification:", notificationInformation);
	};

	useEffect(() => {
		console.log(notificationInformation);
	}, [notificationInformation]);

	const renderEmitNotificationModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseModal}
			size="3xl"
			icon={FiBell}
			title={t("property.emitNotification")}
			subtitle={t("property.emitNotificationSubtitle")}
			footer={
				<ModalFooterActions
					onCancel={handleCloseModal}
					onConfirm={handleSendNotification}
					confirmLabel={t("misc.send")}
					confirmIcon={FiSend}
				/>
			}
		>
			<PropertyModalSection icon={FiInfo} title={t("property.notificationInformation")}>
				<VStack spacing={3} align="stretch">
					<FormControl isRequired>
						<ModalFieldLabel icon={FiFileText}>{t("property.notificationTitle")}</ModalFieldLabel>
						<Input
							size="sm"
							type="text"
							placeholder={t("property.enterNotificationTitle")}
							name="title"
							onChange={handleNotificationInfoChange}
							borderRadius="md"
						/>
					</FormControl>
					<FormControl isRequired>
						<ModalFieldLabel icon={FiFileText}>{t("property.notificationMessage")}</ModalFieldLabel>
						<Textarea
							size="sm"
							placeholder={t("property.enterNotificationMessage")}
							name="message"
							onChange={handleNotificationInfoChange}
							borderRadius="md"
							rows={5}
							resize="vertical"
						/>
					</FormControl>
					<Box pt={1}>
						<Checkbox
							name="notifyOwner"
							size="md"
							colorScheme="blue"
							onChange={(e) => {
								setNotificationInformation({
									...notificationInformation,
									recipients: { ...notificationInformation.recipients, notifyOwner: e.target.checked },
								});
							}}
						>
							<Stack spacing={0}>
								<Text fontSize="sm" fontWeight="medium">
									{t("property.notifyOwner")}
								</Text>
								<Text fontSize="xs" color="gray.500">
									{t("notifications.includeOwner")}
								</Text>
							</Stack>
						</Checkbox>
					</Box>
				</VStack>
			</PropertyModalSection>
		</PropertyModalShell>
	);

	return (
		<Stack spacing={8} p={4}>
			<Flex justify="space-between" align="center">
				<Tooltip label={t("property.addTenantsToEnable")} isDisabled={property.tenants.filter((t) => !t.former).length !== 0}>
					<Button
						onClick={onOpen}
						colorScheme="blue"
						isDisabled={property.tenants.filter((t) => !t.former).length === 0 || isArchived}
						leftIcon={<i className="fas fa-paper-plane" />}
						size="md"
					>
						{t("property.sendNotification")}
					</Button>
				</Tooltip>
				{renderEmitNotificationModal()}
			</Flex>

			<Card variant="outline">
				<CardBody>
					<Heading size="md" mb={6}>
						{t("property.notifications")}
					</Heading>

					{notifications && notifications.length > 0 ? (
						<Table variant="simple">
							<Thead>
								<Tr>
									<Th>{t("table.date")}</Th>
									<Th>{t("table.title")}</Th>
									<Th>{t("table.description")}</Th>
									<Th>{t("table.recipients")}</Th>
								</Tr>
							</Thead>
							<Tbody>
								{notifications
									.slice()
									.reverse()
									.map((n, index) => (
										<Tr key={index}>
											<Td whiteSpace="nowrap" color="gray.600">
												{moment(n.timestamp).format(fullDateTimeFormat)}
											</Td>
											<Td fontWeight="medium">{n.title}</Td>
											<Td maxW="300px" overflow="hidden" textOverflow="ellipsis">
												{n.text || n.message}
											</Td>
											<Td>{n.recipients?.join(", ") || "-"}</Td>
										</Tr>
									))}
							</Tbody>
						</Table>
					) : (
						<Center py={10}>
							<VStack spacing={3}>
								<Icon as={FaBell} boxSize={8} color="gray.400" />
								<Text color="gray.500" fontSize="lg" fontWeight="medium">
									{t("property.noNotifications")}
								</Text>
								<Text color="gray.400" textAlign="center">
									{t("property.notificationsWillAppearHere")}
								</Text>
							</VStack>
						</Center>
					)}
				</CardBody>
			</Card>

			<Card variant="outline">
				<CardBody>
					<Heading size="md" mb={6}>
						{t("property.systemNotifications")}
					</Heading>
					{property.systemNotifications?.length > 0 ? (
						<Table variant="simple">
							<Thead>
								<Tr>
									<Th>{t("table.date")}</Th>
									<Th>{t("table.description")}</Th>
									<Th>{t("table.type")}</Th>
									<Th>{t("table.recipients")}</Th>
								</Tr>
							</Thead>
							<Tbody>
								{property.systemNotifications.map((n, index) => (
									<Tr key={index}>
										<Td whiteSpace="nowrap" color="gray.600">
											{moment(n.timestamp).format(fullDateTimeFormat)}
										</Td>
										<Td>{n.description || "-"}</Td>
										<Td>
											<Badge colorScheme="purple">{n.type}</Badge>
										</Td>
										<Td>{n.recipients?.join(", ") || "-"}</Td>
									</Tr>
								))}
							</Tbody>
						</Table>
					) : (
						<Center py={8}>
							<VStack spacing={3}>
								<Icon as={FaBell} boxSize={8} color="gray.400" />
								<Text color="gray.500" fontSize="lg" fontWeight="medium">
									{t("property.noNotifications")}
								</Text>
								<Text color="gray.400" textAlign="center">
									{t("property.notificationsWillAppearHere")}
								</Text>
							</VStack>
						</Center>
					)}
				</CardBody>
			</Card>
		</Stack>
	);
}
